import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import isDeepEqual from "fast-deep-equal";
import { computed, type MaybeRefOrGetter, toValue } from "vue";

import { debouncedRef } from "@/lib/composables/debounced-ref";
import { useStablePending } from "@/lib/composables/stable-pending";

import { useNewsletterId, useXsrfHeaders } from "../utils";
import { fetchRender } from "./helpers";
import type { PreviewSource, PreviewSourceFactory } from "./types";

interface FeedDraftInput {
  feedId: string;
  subject: string;
  body: string;
}

export const useFeedDraftPreview = (
  input: MaybeRefOrGetter<FeedDraftInput>,
  options: {
    subscriberId?: MaybeRefOrGetter<string | undefined>;
    enabled?: MaybeRefOrGetter<boolean>;
  } = {}
): PreviewSource => {
  const newsletterId = useNewsletterId();
  const xsrfHeaders = useXsrfHeaders();

  const enabled = computed(() => toValue(options.enabled) ?? true);

  const debouncedContent = debouncedRef(
    computed<FeedDraftInput>((prev) => {
      const next = toValue(input);
      if (prev !== undefined && isDeepEqual(prev, next)) {
        return prev;
      }
      return next;
    }),
    500,
    { enabled }
  );

  const queryParams = computed(() => ({
    ...debouncedContent.value,
    subscriberId: toValue(options.subscriberId),
  }));

  const query = useQuery({
    queryKey: [newsletterId, "feed-draft-preview", queryParams],
    enabled,
    gcTime: 10_000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn({ signal }) {
      const params = queryParams.value;
      return fetchRender(
        {
          external_feed_id: params.feedId,
          subject: params.subject,
          text: params.body,
          subscriber_id: params.subscriberId,
          context: "email",
        },
        { signal, headers: xsrfHeaders.value }
      );
    },
  });

  const isStableFetching = useStablePending(query.isFetching);

  return {
    html: query.data,
    error: query.error,
    pending: computed(() => query.isLoading.value || isStableFetching.value),
  };
};

/** @knipignore */
export const feedDraftPreviewSource = (
  input: MaybeRefOrGetter<FeedDraftInput>,
  options: { subscriberId?: MaybeRefOrGetter<string | undefined> } = {}
): PreviewSourceFactory<"feed_draft:email"> => ({
  key: "feed_draft:email",
  create: ({ enabled }) => {
    return useFeedDraftPreview(input, {
      subscriberId: options.subscriberId,
      enabled,
    });
  },
});
